import { isDefined } from 'libs/utils/typesafe/isDefined';
import { useCtxMapView } from 'pages/special/OsloDesignFair/map/CtxMapView';
import { DividedMarkers } from 'pages/special/OsloDesignFair/map/divideMarkers';
import { IndoorStandMarkers } from 'pages/special/OsloDesignFair/map/mapContent/Markers/IndoorStandMarkers';
import { DirectionMarker } from 'pages/special/OsloDesignFair/map/markers/view/DirectionMarker';
import { EmptyLayer } from 'pages/special/OsloDesignFair/map/markers/view/EmptyLayer';
import { ImageTextMarker } from 'pages/special/OsloDesignFair/map/markers/view/ImageTextMarker';
import { LabelMarkers } from 'pages/special/OsloDesignFair/map/markers/view/LabelMarkers';
import { LineMarker } from 'pages/special/OsloDesignFair/map/markers/view/LineMarker';
import { PolygonMarker } from 'pages/special/OsloDesignFair/map/markers/view/PolygonMarker';
import { RouteMarker } from 'pages/special/OsloDesignFair/map/markers/view/RouteMarker';
import { useLoadMapImages } from 'pages/special/OsloDesignFair/map/markers/view/useLoadMapImages';
import { VariantIconMarkers } from 'pages/special/OsloDesignFair/map/markers/view/VariantIconMarkers';
import { WallMarker } from 'pages/special/OsloDesignFair/map/markers/view/WallMarker';
import { GroundFloor } from 'pages/special/OsloDesignFair/map/markers/view/WallMarker/GroundFloor';
import { GroupWallMarkers } from 'pages/special/OsloDesignFair/map/markers/view/WallMarker/GroupWallMarkers';
import { useEffect } from 'react';
import { Source } from 'react-map-gl';

interface Props {
    dividedMarkers: DividedMarkers;
}

export const WorldMarkers = (props: Props): JSX.Element => {
    const ctxMapView = useCtxMapView();

    const {
        floorMarkers,
        markers: markerGroups,
        variantIconMarkers,
        indoorStands,
        labelMarkers
    } = props.dividedMarkers;

    const highlightedMarkerIds = ctxMapView.highlightedMarkerIds;

    const layerOrderMarkerGroups = markerGroups.map((e) =>
        e.type === 'wallGroup' ? 'wall_group|' + e.id : e.id
    );

    const layerOrder = layerOrderMarkerGroups.concat(floorMarkers.map((e) => e.id + '|floor'));

    const { isLoaded } = useLoadMapImages({
        mapRef: ctxMapView.mapRef,
        imageUrls: indoorStands.map((e) => e.logoUrl).filter(isDefined)
    });

    useEffect(() => {
        const mapRef = ctxMapView.mapRef.current;
        if (!mapRef) {
            return;
        }

        for (let i = 1; i < layerOrder.length; i++) {
            const firstId = layerOrder[i] + '|layer';
            const secondId = layerOrder[i - 1] + '|last';

            if (mapRef.getLayer(firstId) && mapRef.getLayer(secondId)) {
                mapRef.moveLayer(firstId, secondId);
            }
        }
    }, [layerOrder.join(';')]);

    if (!isLoaded) {
        return null;
    }

    return (
        <>
            <Source
                id={'empty-source'}
                type="geojson"
                data={{ type: 'FeatureCollection', features: [] }}
            />
            <VariantIconMarkers markers={variantIconMarkers} />
            {markerGroups.map((markerGroup, index) => {
                const beforeMarkerId = layerOrder[index - 1];
                const beforeId = beforeMarkerId ? beforeMarkerId + '|last' : undefined;
                const orderIndex = markerGroups.length - index;

                if (markerGroup.type === 'wallGroup') {
                    const groupWallMarkers = markerGroup.markers;
                    return (
                        <GroupWallMarkers
                            key={markerGroup.id}
                            id={markerGroup.id}
                            markers={groupWallMarkers}
                            beforeId={beforeId}
                        />
                    );
                }

                const marker = markerGroup.marker;
                const isHighlighted =
                    (ctxMapView.forceHighlightSelectableMarkers && marker.selectable) ||
                    highlightedMarkerIds.includes(marker.id);

                switch (marker.__typename) {
                    case 'MapMarkerLine':
                        return (
                            <LineMarker
                                key={marker.id}
                                marker={marker}
                                beforeId={beforeId}
                                orderIndex={orderIndex}
                                isHighlighted={isHighlighted}
                            />
                        );
                    case 'MapMarkerPolygon':
                        return (
                            <PolygonMarker
                                key={marker.id}
                                marker={marker}
                                beforeId={beforeId}
                                mapRef={ctxMapView.mapRef}
                                orderIndex={orderIndex}
                                isHighlighted={isHighlighted}
                            />
                        );
                    case 'MapMarkerWall':
                        return (
                            <WallMarker
                                key={marker.id}
                                marker={marker}
                                beforeId={beforeId}
                                mapRef={ctxMapView.mapRef}
                                orderIndex={orderIndex}
                            />
                        );
                    case 'MapMarkerImage':
                    case 'MapMarkerText':
                        return (
                            <ImageTextMarker
                                key={marker.id}
                                marker={marker}
                                beforeId={beforeId}
                                orderIndex={orderIndex}
                                mapRef={ctxMapView.mapRef}
                                isHighlighted={isHighlighted}
                            />
                        );
                    case 'MapMarkerDirection':
                        return (
                            <DirectionMarker
                                key={marker.id + '|' + marker.coordinates.length}
                                marker={marker}
                                mapRef={ctxMapView.mapRef}
                                beforeId={beforeId}
                                orderIndex={orderIndex}
                            />
                        );
                    case 'MapMarkerRoute':
                        return (
                            <RouteMarker
                                key={marker.id + '|' + marker.coordinates.length}
                                marker={marker}
                                mapRef={ctxMapView.mapRef}
                                beforeId={beforeId}
                                orderIndex={orderIndex}
                            />
                        );
                    default:
                        return null;
                }
            })}
            <EmptyLayer
                id={'nonIcons|last'}
                beforeId={
                    layerOrderMarkerGroups.length
                        ? layerOrderMarkerGroups[layerOrderMarkerGroups.length - 1] + '|last'
                        : undefined
                }
            />
            {floorMarkers.map((marker, index) => {
                const beforeMarkerId = floorMarkers[index - 1]?.id;
                const beforeId = beforeMarkerId ? beforeMarkerId + '|floor|last' : 'nonIcons|last';
                const orderIndex = markerGroups.length + index;

                return (
                    <GroundFloor
                        key={marker.id + '|floor'}
                        markerId={marker.id}
                        beforeId={beforeId}
                        coordinates={marker.transformedCoordinates}
                        color={marker.floorColor}
                        opacity={marker.floorOpacity}
                        orderIndex={orderIndex}
                        visible={!marker.isHidden}
                    />
                );
            })}
            <EmptyLayer
                id={'afterFloorMarkers|last'}
                beforeId={
                    floorMarkers.length
                        ? floorMarkers[floorMarkers.length - 1].id + '|floor|last'
                        : 'nonIcons|last'
                }
            />
            <IndoorStandMarkers markers={indoorStands} />
            <LabelMarkers markers={labelMarkers} />
        </>
    );
};
